import { useCallback } from 'react'
import * as Sentry from '@sentry/electron/renderer'
import AriesIpcRenderer from '@renderer/ipc/AriesIpc.renderer'
import { isMac } from '@shared/utils/platform'
import type { SystemAudioCaptureStatus } from '@shared/types/Aries'
import type { PermissionsState } from '@shared/types/AudioCapturePermissions'
import type { SystemAudioActionKind } from './audio-permission-prompt'
import { useMirroredPermission } from './use-mirrored-permission'

const checkSystemAudioPerm = (): Promise<SystemAudioCaptureStatus> => AriesIpcRenderer.checkSystemAudioCapturePerm()
const selectSystemAudioStatus = (permissions: PermissionsState): SystemAudioCaptureStatus | undefined =>
  permissions.systemAudioCaptureStatus

/**
 * Tracks the macOS "System Audio Recording" (kTCCServiceAudioCapture) permission
 * via the main-owned permission mirror.
 *
 *  - `request()` covers every actionable state: `notDetermined` triggers the
 *    native prompt, `denied` deep-links to System Settings (macOS won't
 *    re-prompt), and `error` re-reads the status (retry). All surfaces call
 *    this one entry point; `actionKind` tells them which wording to show.
 */
export function useSystemAudioPermission(enabled = true) {
  const { status, check, isLoaded } = useMirroredPermission<SystemAudioCaptureStatus>({
    select: selectSystemAudioStatus,
    checkPerm: checkSystemAudioPerm,
    fallback: 'error',
    enabled,
  })

  const actionKind: SystemAudioActionKind = status === 'error' ? 'retry' : 'request'

  const request = useCallback(async () => {
    if (!isMac || !enabled) {
      return
    }
    try {
      if (status === 'error') {
        await check()
        return
      }
      const current = status ?? (await check())
      if (current === 'error' || current === 'unavailable') {
        return
      }
      const outcome = await AriesIpcRenderer.requestSystemAudioCapturePerm()
      if (outcome === 'openSettings') {
        await AriesIpcRenderer.openSystemSettings('systemAudio')
      } else if (outcome === 'error') {
        Sentry.captureMessage('System audio permission request failed')
      }
      await check()
    } catch (error) {
      Sentry.captureException(error)
    }
  }, [check, enabled, status])

  return {
    status,
    actionKind,
    isAuthorized: status === 'authorized',
    /** Applicable unless this OS has no CATAP gate (`unavailable`) or status is still loading. */
    isApplicable: status !== undefined && status !== 'unavailable',
    isLoaded,
    check,
    request,
  }
}
